import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import axios from 'axios';
import { CandidateProfile, SessionResponse } from '../types';
import {
  loginWithPassword,
  requestMagicLink,
  verifyMagicLink,
} from '../api/candidate';
import { resetCsrfToken } from '../api/csrf';

interface AuthState {
  token: string | null;
  refreshToken: string | null;
  tokenExpiresAt: number | null;
  candidate: CandidateProfile | null;
  /** État du flux de connexion par lien magique. */
  linkRequested: boolean;
  authError: string | null;
  requesting: boolean;
  verifying: boolean;
  loggingIn: boolean;

  login: (email: string, password: string) => Promise<boolean>;
  requestLink: (email: string) => Promise<void>;
  verify: (token: string) => Promise<boolean>;
  setSession: (session: SessionResponse) => void;
  setCandidate: (candidate: CandidateProfile) => void;
  logout: () => void;
  clearAuth: () => void;
  resetFlow: () => void;
}

function extractMessage(error: unknown, fallback: string): string {
  if (
    axios.isAxiosError(error) &&
    typeof error.response?.data === 'object' &&
    error.response?.data !== null
  ) {
    const msg = (error.response.data as Record<string, unknown>).message;
    if (typeof msg === 'string' && msg) return msg;
  }
  return fallback;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      token: null,
      refreshToken: null,
      tokenExpiresAt: null,
      candidate: null,
      linkRequested: false,
      authError: null,
      requesting: false,
      verifying: false,
      loggingIn: false,

      login: async (email, password) => {
        set({ loggingIn: true, authError: null });
        try {
          const data = await loginWithPassword(email, password);
          set({
            token: data.accessToken,
            refreshToken: data.refreshToken,
            tokenExpiresAt: Date.now() + data.expiresIn * 1000,
            candidate: data.candidate,
            linkRequested: false,
          });
          return true;
        } catch (error) {
          set({
            authError: extractMessage(
              error,
              'Email ou mot de passe incorrect.',
            ),
          });
          return false;
        } finally {
          set({ loggingIn: false });
        }
      },

      requestLink: async (email) => {
        set({ requesting: true, authError: null, linkRequested: false });
        try {
          await requestMagicLink(email);
          set({ linkRequested: true });
        } catch (error) {
          set({
            authError: extractMessage(
              error,
              "Impossible d'envoyer le lien pour le moment. Réessayez plus tard.",
            ),
          });
        } finally {
          set({ requesting: false });
        }
      },

      verify: async (token) => {
        set({ verifying: true, authError: null });
        try {
          const data = await verifyMagicLink(token);
          set({
            token: data.accessToken,
            refreshToken: data.refreshToken,
            tokenExpiresAt: Date.now() + data.expiresIn * 1000,
            candidate: data.candidate,
            linkRequested: false,
          });
          return true;
        } catch (error) {
          set({
            authError: extractMessage(
              error,
              'Ce lien de connexion est invalide ou a expiré. Demandez-en un nouveau.',
            ),
          });
          return false;
        } finally {
          set({ verifying: false });
        }
      },

      setSession: (session) =>
        set({
          token: session.accessToken,
          refreshToken: session.refreshToken,
          tokenExpiresAt: Date.now() + session.expiresIn * 1000,
          candidate: session.candidate,
        }),

      setCandidate: (candidate) => set({ candidate }),

      logout: () => get().clearAuth(),

      clearAuth: () => {
        resetCsrfToken();
        set({
          token: null,
          refreshToken: null,
          tokenExpiresAt: null,
          candidate: null,
          linkRequested: false,
          authError: null,
        });
      },

      resetFlow: () => set({ linkRequested: false, authError: null }),
    }),
    {
      name: 'pmd-candidat-auth',
      partialize: (state) => ({
        token: state.token,
        refreshToken: state.refreshToken,
        tokenExpiresAt: state.tokenExpiresAt,
        candidate: state.candidate,
      }),
    },
  ),
);

/**
 * Une session existe dès qu'un token d'accès est présent. Même expiré, il sera
 * rafraîchi silencieusement au premier 401 via le refresh token (cf. http.ts).
 */
export function hasValidSession(state: { token: string | null }): boolean {
  return !!state.token;
}
